// hooks/useClients.js
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectAllClients,
  selectCurrentClient,
  selectSelectedClientId,
  selectClientsLoading,
  selectClientId
} from "../../store/slices/clients.slice";
import { fetchClient } from "../../api/clientsApi";

/**
 * Custom hook for managing clients in Redux.
 */
export function useClients() {
  const dispatch = useDispatch();

  // Redux selectors
  const allClients = useSelector(selectAllClients);
  const currentClient = useSelector(selectCurrentClient);
  const selectedClientId = useSelector(selectSelectedClientId);
  const isLoading = useSelector(selectClientsLoading);

  /**
   * Switch active client in Redux (i.e. set `selectedClientId`).
   * If you also want to fetch the full doc, you can do that here or
   * in a useEffect that watches selectedClientId changes.
   */
  const switchClient = useCallback(
    (clientId) => {
      // 1) Dispatch the selection
      dispatch(selectClientId(clientId));
      // 2) Optionally fetch if needed
      dispatch(fetchClient(clientId));
    },
    [dispatch]
  );

  /**
   * Alternatively, you could use a useEffect that, whenever `selectedClientId`
   * changes, we fetch the full doc if it's not already loaded or doesn't match current:
   *
   * useEffect(() => {
   *   if (selectedClientId && selectedClientId !== currentClient?.client_id) {
   *     dispatch(fetchClient(selectedClientId));
   *   }
   * }, [selectedClientId, currentClient, dispatch]);
   */

  // Convenience
  const hasActiveClient = Boolean(selectedClientId);
  const currentClientId = currentClient?.client_id || null;

  return {
    allClients,
    currentClient,
    selectedClientId,
    currentClientId,
    hasActiveClient,
    isLoading,
    switchClient
  };
}
